import { Injectable } from '@angular/core';

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  StorePaymentAPI,
  StorePurchaseApi,
  StoreTaxExemptionAPI,
  StoreValidateAddressAPI,
} from '../store-api';
import { ApiService } from './api.service';

@Injectable()
export class StoreApiService
  extends ApiService<any>
  implements
    StoreValidateAddressAPI,
    StorePaymentAPI,
    StoreTaxExemptionAPI,
    StorePurchaseApi
{
  protected APIUrl =
    environment.SPRING_STORE_ENDPOINT_URL + this.getResourceUrl();

  private readonly taxExemptionUrl =
    environment.SPRING_ENDPOINT_URL + '/v2/tax-exemption';

  constructor(protected httpClient: HttpClient) {
    super(httpClient);
  }

  getResourceUrl(): string {
    return '/v2';
  }

  validateAddress(addressObject) {
    const params = new HttpParams()
      .set('street', addressObject.street)
      .set('unit', addressObject.unit)
      .set('city', addressObject.city)
      .set('country', addressObject.country)
      .set('postalCode', addressObject.postalCode)
      .set('province', addressObject.province);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'validate_address', params),
    );
  }

  estimatePurchase(jsonData) {
    const params = new HttpParams().set('jsonData', jsonData);

    return firstValueFrom(
      super.requestAsObservable('', 'GET', 'purchase/estimate', params),
    );
  }

  preparePurchase(jsonData) {
    const params = new HttpParams().set('jsonData', jsonData);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'purchase/prepare', params),
    );
  }

  purchase(jsonData) {
    const params = new HttpParams().set('jsonData', jsonData);

    return firstValueFrom(
      super.requestAsObservable('', 'POST', 'purchase/put', params),
    );
  }

  preparePayment(paymentMethodId, invoiceId, companyId, token) {
    const headers = new HttpHeaders().set('token', token);
    const params = new HttpParams()
      .set('paymentMethodId', paymentMethodId)
      .set('companyId', companyId);

    return firstValueFrom(
      super.requestWithHeadersAsObservable(
        '',
        'POST',
        `payment/prepare/${invoiceId}`,
        headers,
        params,
      ),
    );
  }

  collectPayment(paymentIntentId, invoiceId, companyId, token) {
    const headers = new HttpHeaders().set('token', token);
    const params = new HttpParams()
      .set('paymentIntentId', paymentIntentId)
      .set('companyId', companyId);

    return firstValueFrom(
      super.requestWithHeadersAsObservable(
        '',
        'POST',
        `payment/collect/${invoiceId}`,
        headers,
        params,
      ),
    );
  }

  addTaxExemption(companyId, taxExemption, blobKey) {
    const params = new HttpParams()
      .set('companyId', companyId)
      .set('blobKey', blobKey)
      .set('number', taxExemption.number);

    return firstValueFrom(
      this.httpClient.post(this.taxExemptionUrl, null, { params }),
    );
  }

  uploadTaxExemptionCertificate(file) {
    var formData = new FormData();

    formData.append('file', file);

    return firstValueFrom(
      this.httpClient.get<{ item: string }>(
        `${this.taxExemptionUrl}/upload-url`,
      ),
    )
      .catch((error) => {
        console.error('Error getting upload url: ', error);
        return Promise.reject(error);
      })
      .then((resp) => {
        return firstValueFrom(
          this.httpClient.post(resp.item, formData, {
            withCredentials: true,
            responseType: 'text',
          }),
        )
          .then((response: any) => {
            return response;
          })
          .catch((error) => {
            console.error('Error uploading file: ', error);
            return Promise.reject(error);
          });
      });
  }
}
